/**
 * Мини-калькулятор для поля суммы (F-12).
 *
 * Пользователь может ввести «12000+3500*2» или «60000/2» прямо в поле денег.
 * Здесь свой разбор выражения, а не eval — чужой ввод не должен исполняться.
 * Поддерживаются: + − * / ( ), десятичная точка и запятая, пробелы.
 */

import { normalizeNumericString } from './money';

type Token = { type: 'number'; value: number } | { type: 'op'; value: string };

const OPERATORS: Record<string, { precedence: number; apply: (a: number, b: number) => number }> = {
  '+': { precedence: 1, apply: (a, b) => a + b },
  '-': { precedence: 1, apply: (a, b) => a - b },
  '*': { precedence: 2, apply: (a, b) => a * b },
  '/': { precedence: 2, apply: (a, b) => (b === 0 ? NaN : a / b) },
};

export function isExpression(input: string): boolean {
  const body = input.trim().replace(/^[-+]/, '');
  return /[+\-*/()]/.test(body);
}

function tokenize(input: string): Token[] | null {
  const source = normalizeNumericString(input);
  const tokens: Token[] = [];
  let index = 0;

  while (index < source.length) {
    const char = source[index]!;

    if (/\d|\./.test(char)) {
      let numberText = '';
      while (index < source.length && /[\d.]/.test(source[index]!)) {
        numberText += source[index];
        index += 1;
      }
      const value = Number(numberText);
      if (!Number.isFinite(value)) return null;
      tokens.push({ type: 'number', value });
      continue;
    }

    if (char in OPERATORS || char === '(' || char === ')') {
      // Унарный минус: в начале выражения или после другого оператора/скобки.
      const previous = tokens[tokens.length - 1];
      const isUnary =
        (char === '-' || char === '+') &&
        (!previous || (previous.type === 'op' && previous.value !== ')'));
      if (isUnary) {
        tokens.push({ type: 'number', value: 0 });
      }
      tokens.push({ type: 'op', value: char });
      index += 1;
      continue;
    }

    return null; // неизвестный символ
  }

  return tokens;
}

/**
 * Вычисляет выражение. Возвращает null, если ввод некорректен —
 * тогда поле показывает ошибку, а не молча подставляет ноль.
 */
export function evaluateExpression(input: string): number | null {
  if (!input || !input.trim()) return null;
  const tokens = tokenize(input);
  if (!tokens || tokens.length === 0) return null;

  const values: number[] = [];
  const ops: string[] = [];

  const applyTop = (): boolean => {
    const op = ops.pop();
    const b = values.pop();
    const a = values.pop();
    if (op === undefined || a === undefined || b === undefined) return false;
    const handler = OPERATORS[op];
    if (!handler) return false;
    values.push(handler.apply(a, b));
    return true;
  };

  for (const token of tokens) {
    if (token.type === 'number') {
      values.push(token.value);
      continue;
    }
    if (token.value === '(') {
      ops.push(token.value);
      continue;
    }
    if (token.value === ')') {
      while (ops.length && ops[ops.length - 1] !== '(') {
        if (!applyTop()) return null;
      }
      if (ops.pop() !== '(') return null;
      continue;
    }
    const current = OPERATORS[token.value];
    if (!current) return null;
    while (ops.length) {
      const top = ops[ops.length - 1]!;
      const topOp = OPERATORS[top];
      if (!topOp || topOp.precedence < current.precedence) break;
      if (!applyTop()) return null;
    }
    ops.push(token.value);
  }

  while (ops.length) {
    if (ops[ops.length - 1] === '(') return null;
    if (!applyTop()) return null;
  }

  if (values.length !== 1) return null;
  const result = values[0]!;
  return Number.isFinite(result) ? result : null;
}

/**
 * Разбор пользовательского ввода суммы: и обычное число, и выражение.
 * Возвращает число в основных единицах (не минорных).
 */
export function parseAmountInput(input: string): number | null {
  if (!input || !input.trim()) return null;
  if (isExpression(input)) return evaluateExpression(input);
  const value = Number(normalizeNumericString(input));
  return Number.isFinite(value) ? value : null;
}
